export default function ZipVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - ZipVEdit Archive Manager
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-04-22">April 22, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>ZipVEdit</strong> as an
            ad-supported Android archive manager. This Privacy Policy
            explains what information ZipVEdit may process, how that
            information is used, and the choices you have when using the
            app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Archive files and their contents:</strong> archives
              you open, extract, or create are read and written entirely on
              your device. File bytes never leave your phone.
            </li>
            <li>
              <strong>Files shared to ZipVEdit:</strong> when you send files
              from another app using Android&apos;s share sheet, those files
              are processed locally to create an archive. The files are not
              uploaded anywhere.
            </li>
            <li>
              <strong>Passwords you enter:</strong> passwords used to open
              or create protected archives are held in memory for the
              duration of the operation. If you opt in to &quot;Remember
              password&quot;, passwords are stored on your device only —
              never transmitted.
            </li>
            <li>
              <strong>Recent archive history:</strong> the paths of archives
              you recently opened are stored locally so they appear on the
              home screen. You can clear this list from Settings.
            </li>
            <li>
              <strong>App preferences:</strong> theme choice, language, and
              settings are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and user
              experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>To open, preview, extract, and create archive files.</li>
            <li>
              To process files shared from other apps via the system share
              sheet.
            </li>
            <li>
              To remember your recent archives and settings so the app
              launches ready for you.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            ZipVEdit is designed to keep your data on your device. Archive
            files, extracted contents, recent history, and preferences are
            stored locally and are not uploaded to any server. Data remains
            on your device until you remove it, clear the app&apos;s
            storage, or uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access
          </h2>
          <p>
            Opening, extracting, creating, and previewing archives does not
            require network access. The app makes network requests only for
            ads, Firebase analytics and crash reporting, and the rotating
            cross-promotion banner on the home screen. No archive contents,
            file bytes, or passwords are ever transmitted.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Biometric Unlock
          </h2>
          <p>
            If your device supports biometric unlock (fingerprint or face
            unlock) you can enable &quot;Unlock with biometrics&quot; when
            saving a password. Biometric data is handled entirely by the
            Android system — ZipVEdit never sees your fingerprint or face.
            We only ask the system to confirm your identity before reading
            the password you previously saved on this device.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            ZipVEdit may use <strong>Google AdMob</strong> to show banner,
            interstitial, and rewarded ad formats. AdMob may collect and
            process identifiers, device information, approximate location,
            and ad interaction data to deliver and measure ads. You can
            remove ads via a one-time Pro upgrade.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            ZipVEdit offers an optional one-time Pro upgrade. The Pro tier
            removes advertisements, enables AES-256 encryption for new
            archives, batch compression, cloud destinations, and unlimited
            output to advanced formats. Purchases are processed through
            Google Play and are subject to Google&apos;s terms and privacy
            policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your files or personal data. Data may be shared
            only with service providers required to operate app features,
            such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it,
            clear the app&apos;s storage, or uninstall the app. Data
            collected by third-party services such as AdMob and Firebase is
            retained according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            ZipVEdit is not intended for children under 13, and we do not
            knowingly collect personal information from children through
            the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can clear the recent-archives list from inside the app.
            </li>
            <li>
              You can forget a remembered password at any time in Settings.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data.
            </li>
            <li>
              You can purchase the Pro upgrade to disable all advertising.
            </li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we
            do, we will post the updated version on this page and revise
            the effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about ZipVEdit, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
